import classNames from 'classnames';
import React from 'react';
import { useState } from 'react';
import { createUseStyles } from 'react-jss';
import variables from '../../theme/variables';
import TransitionText from '../common/TransitionText';
import { ReactComponent as CloseIcon } from '../../assets/icons/CloseIcon.svg';
import { useEffect } from 'react';
import { useRef } from 'react';

const useStyles = createUseStyles(
    {
        wrap: {
            // position: 'absolute',
            transition: 'transform 0.3s ease-in-out',
            cursor: 'pointer',
            perspective: '1000px',
            backfaceVisibility: 'hidden',
            '-webkit-font-smoothing': 'antialiased',
            transform: 'scale(calc(1 / var(--scaleFactor))) perspective(1px)',

            '@media (min-width: 1024px)': {
                '&:hover': {
                    transform: 'scale(1) perspective(1px)',
                    zIndex: 100,
                },
            },

            '&$selected': {
                zIndex: 100,

                '@media (min-width: 1024px)': {
                    transform: 'scale(1) perspective(1px)',
                },
            },
        },

        inner: {
            position: 'relative',
            width: '100%',
            height: '100%',
            // borderRadius: 10,
            transition: 'transform 0.3s ease-in-out',
            transformStyle: 'preserve-3d',

            '&$selected': {
                transform: 'rotateY(180deg)',
            },
        },

        selected: {},

        front: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            // borderRadius: 10,
            overflow: 'hidden',
        },

        image: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center center',
        },

        back: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: variables.palette.white,
            transform: 'rotateY(180deg)',
            backfaceVisibility: 'hidden',
            // borderRadius: 10,
            color: variables.palette.black,
        },

        backInner: {
            height: '100%',
            width: '100%',
            padding: '25px 25px 10px 25px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            paddingRight: '1em',
            position: 'relative',
        },

        shadow: {
            left: 0,
            width: 'calc(100% - 20px)',
            bottom: 0,
            height: 50,
            position: 'absolute',
            background:
                'linear-gradient(180deg, rgba(255, 255, 255, 0), #FFFFFF)',
            pointerEvents: 'none',
        },

        backTitle: {
            fontSize: 24,
            lineHeight: '30px',
            marginBottom: 18,
        },

        backCopy: {
            fontSize: 16,
            lineHeight: '22px',

            '& p': {
                marginBottom: '1em',

                '&:last-of-type': {
                    paddingBottom: 50,
                },
            },
        },

        closeBtn: {
            position: 'absolute',
            top: 8,
            right: 8,
            transform: 'scale(0.50)',
            zIndex: 1,

            '& svg path': {
                fill: variables.palette.black,
            },

            '@media (min-width: 1024px)': {
                top: 0,
                left: 'calc(100% + 30px / var(--scaleFactor))',
                transform: 'scale(1)',
                transformOrigin: 'top left',
                transition: 'opacity 0.3s ease-in-out',

                '& svg path': {
                    fill: variables.palette.white,
                },
            },
        },
    },
    { name: 'InspirationCard' }
);

export default function InspirationCard({
    card,
    selected,
    onClose,
    onOpen,
    layout,
}) {
    const classes = useStyles();
    const [scaleFactor, setScaleFactor] = useState(1);
    const [position, setPosition] = useState({});

    const backContainerRef = useRef();

    const handleCardClicked = () => {
        if (!selected) {
            onOpen(card);
        }
    };

    useEffect(() => {
        if (!selected) {
            setTimeout(() => {
                if (backContainerRef.current) {
                    backContainerRef.current.scroll(0, 0);
                }
            }, 200);
        }
    }, [selected]);

    useEffect(() => {
        if (window.innerWidth < 1024) {
            setScaleFactor(1);
            setPosition({
                left: 0,
                top: 0,
                width: 'calc(100% - 40px)',
                maxWidth: 500,
                margin: '0 auto',
                height: 'auto',
                aspectRatio: `${card.position.desktop.width} / ${card.position.desktop.height}`,
            });
        } else {
            let scale = Math.max(
                480 /
                    Math.max(
                        card.position.desktop.width,
                        card.position.desktop.height
                    ),
                1.3
            );
            setScaleFactor(scale);
            setPosition({
                width: card.position.desktop.width * scale,
                height: card.position.desktop.height * scale,
                top:
                    card.position.desktop.top -
                    (card.position.desktop.height * scale -
                        card.position.desktop.height) /
                        2,
                left:
                    card.position.desktop.left -
                    (card.position.desktop.width * scale -
                        card.position.desktop.width) /
                        2,
            });
        }
    }, []);

    return (
        <div
            className={classNames(classes.wrap, selected && classes.selected)}
            style={{
                position: layout === 'desktop' ? 'absolute' : 'relative',
                ...position,
                '--scaleFactor': scaleFactor,
            }}
            // onMouseEnter={() => setTitleState('in')}
            // onMouseLeave={() => setTitleState('init')}
            onClick={handleCardClicked}
        >
            <button
                className={classes.closeBtn}
                style={{ opacity: selected ? 1 : 0 }}
                onClick={onClose}
            >
                <CloseIcon />
            </button>
            <div
                className={classNames(
                    classes.inner,
                    selected && classes.selected
                )}
            >
                <div className={classes.front}>
                    <img
                        src={card.image}
                        alt={card.title}
                        className={classes.image}
                    />
                </div>

                <div className={classes.back}>
                    <div className={classes.backInner} ref={backContainerRef}>
                        <h4
                            className={classes.backTitle}
                            dangerouslySetInnerHTML={{ __html: card.title }}
                        ></h4>
                        <div className={classes.backCopy}>{card.copy}</div>
                    </div>
                    <div className={classes.shadow}></div>
                </div>
            </div>
        </div>
    );
}
